import React, {useState} from 'react';
import Input from '../../../components/common/input/Input';
import Button from '../../../components/common/button/Button';
import FileInput from '../../../components/common/input/FileInput';
import styles from './RegisterPage.module.css';
import {checkAPI, mailAPI, registerAPI, requestAPI} from "../../../services/auth";
import {useNavigate} from "react-router-dom";
import {CheckIcon} from '@heroicons/react/20/solid';
import {useAlert} from "../../../components/common/error/AlertContext";
import BusinessNumberPopUp from "../../client/popup/BusinessNumberPopUp";

interface RegisterForm {
    companyName: string;
    username: string;
    email: string;
    name: string;
    managerName: string;
    phoneNumber: string;
    password: string;
    registrationNumber: string;
    check: boolean;
    license: File | null;
}

//개인정보 선택사항 수집 미동의 회원가입 페이지
const EssentialRegisterPage: React.FC = () => {
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const navigate = useNavigate();
    const {showAlert} = useAlert();

    const [formData, setFormData] = useState<RegisterForm>({
        companyName: '',
        username: '',
        email: '',
        password: '',
        registrationNumber: '',
        check: false,
        license: null,
        name: '',
        managerName: '',
        phoneNumber: ''
    });

    const handelGoBack = () => {
        navigate('/register/term');
    }

    //이메일 형식 확인
    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    //전화번호 형식 확인
    const isValidPhoneNumber = (phone: string) => {
        // 한국 휴대폰 번호 패턴: 010-1234-5678, 010.1234.5678, 01012345678
        const phoneRegex = /^(010|070|011|016|017|018|019)[-.]?\d{3,4}[-.]?\d{4}$/;
        return phoneRegex.test(phone);
    };

    //전화번호 형식화 (자동으로 하이픈 추가)
    const formatPhoneNumber = (phone: string) => {
        // 숫자만 추출
        const numbers = phone.replace(/[^\d]/g, '');

        // 010으로 시작하는 경우
        if (numbers.startsWith('010') || numbers.startsWith('070')) {
            if (numbers.length <= 3) return numbers;
            if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
            return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
        }

        // 기타 번호 (011, 016, 017, 018, 019)
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    };

    //사업자등록번호 형식화 (자동으로 하이픈 추가)
    const formatBusinessNumber = (businessNumber: string) => {
        // 숫자만 추출
        const numbers = businessNumber.replace(/[^\d]/g, '');

        // 사업자등록번호 형식: 000-00-00000 (3-2-5 자리)
        if (numbers.length <= 3) {
            return numbers;
        } else if (numbers.length <= 5) {
            return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        } else {
            return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
        }
    };

    //전화번호 유효성 검사
    const validatePhoneNumber = (phone: string) => {
        if (!phone.trim()) {
            return '전화번호를 입력해주세요.';
        }

        const numbers = phone.replace(/[^\d]/g, '');

        // 길이 검사
        if (numbers.length < 10 || numbers.length > 11) {
            return '전화번호는 10-11자리 숫자여야 합니다.';
        }

        // 유효한 시작 번호 검사
        if (!['010', '011', '016', '017', '018', '019', '070'].some(prefix => numbers.startsWith(prefix))) {
            return '유효하지 않은 전화번호 형식입니다.';
        }

        // 전체 형식 검사
        if (!isValidPhoneNumber(phone)) {
            return '올바른 전화번호 형식이 아닙니다.';
        }

        return null;
    };

    //사업자등록번호 인증 성공 시 번호 기입
    const handleAuthSuccess = (businessNumber: string, name: string, companyName: string) => {
        setFormData(prev => ({
            ...prev,
            registrationNumber: formatBusinessNumber(businessNumber), // 포맷팅 적용
            name: name,
            companyName: companyName
        }));
    };

    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [code, setCode] = useState<string>('');
    const [emailVerified, setEmailVerified] = useState<boolean>(false);

    //사업자등록증 등록
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setFormData(prev => ({...prev, license: file}));
    };

    //입력 (전화번호, 사업자등록번호 자동 포맷팅 포함)
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;

        if (name === 'phoneNumber') {
            // 전화번호 입력 시 자동 포맷팅
            const formattedPhone = formatPhoneNumber(value);
            setFormData(prev => ({...prev, [name]: formattedPhone}));
        } else if (name === 'registrationNumber') {
            // 사업자등록번호 입력 시 자동 포맷팅
            const formattedBusinessNumber = formatBusinessNumber(value);
            setFormData(prev => ({...prev, [name]: formattedBusinessNumber}));
        } else {
            setFormData(prev => ({...prev, [name]: value}));
        }
    };

    //비밀번호 확인 입력
    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
    };

    //이메일 인증 코드 입력
    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCode(e.target.value);
    };

    //회원가입
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        //ID, PW, 사업자등록번호, 이메일, 사업자등록증, 전화번호 인증
        if (formData.username.length > 10 || formData.username.length < 4) {
            showAlert("ID는 4자 이상, 10자 이하여야 합니다.", "error");
            return;
        }
        if (formData.password !== confirmPassword) {
            showAlert('비밀번호가 일치하지 않습니다', "error");
            return;
        }
        const passwordError = validatePassword(formData.password);
        if (passwordError) {
            showAlert(passwordError, "error");
            return;
        }
        // 전화번호 유효성 검사 추가
        const phoneError = validatePhoneNumber(formData.phoneNumber);
        if (phoneError) {
            showAlert(phoneError, "error");
            return;
        }
        if (!formData.registrationNumber.trim()) {
            showAlert('사업자등록번호가 인증되지 않았습니다', "error");
            return;
        }
        if (!formData.check) {
            showAlert('이메일 인증이 완료되지 않았습니다', "error");
            return;
        }
        if (!formData.license) {
            showAlert('사업자등록증을 첨부해주세요', "error");
            return;
        }
        try {
            const registerData = {
                companyName: formData.companyName,
                username: formData.username,
                email: formData.email,
                password: formData.password,
                businessNumber: formData.registrationNumber,
                name: formData.name,
                managerName: formData.managerName,
                position: '',
                phoneNumber: formData.phoneNumber,
                department: '',
                type: '',
                item: '',
                address: '',
                optional: false
            };
            //회원가입 API
            const response = await registerAPI(registerData, formData.license);
            //계정 처리 요청 생성 API
            await requestAPI(response);
            //로그인 화면으로 이동
            navigate('/login');
        } catch (error) {
            console.log(error);
        }
    };

    //비밀번호 조건 확인
    const validatePassword = (password: string) => {
        //최소 8자
        const minLength = 8;
        //문자 사용
        const hasLetter = /[a-zA-Z]/.test(password);
        //숫자 사용
        const hasNumber = /[0-9]/.test(password);
        //특수기호 사용
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        //에러 메세지
        if (password.length < minLength) {
            return '비밀번호는 8자 이상이어야 합니다.';
        }
        if (!hasLetter) {
            return '비밀번호에 문자가 포함되어야 합니다.';
        }
        if (!hasNumber) {
            return '비밀번호에 숫자가 포함되어야 합니다.';
        }
        if (!hasSpecial) {
            return '비밀번호에 특수문자가 포함되어야 합니다.';
        }
        return null;
    };

    const mailHandler = async () => {
        if (!isValidEmail(formData.email)) {
            showAlert("이메일 형식이 잘못되었습니다.", "error");
            return;
        }
        try {
            //인증 코드 발송 API
            await mailAPI(formData.email);
            showAlert("인증코드가 전송되었습니다.", "info");
        } catch (error) {
            console.log(error);
        }
    };

    const codeHandler = async () => {
        try {
            //이메일 인증 API
            await checkAPI(formData.email, code);
            setFormData(prev => ({...prev, check: true}));
            setEmailVerified(true);
        } catch (error) {
            console.log(error);
        }
    };

    const authenticateHandler = () => {
        setIsAuthModalOpen(true);
    };

    return (
        <div>
            <div className={styles.container}>
                <div className={styles.formWrapper}>
                    <h1 className={styles.title}>회원가입</h1>
                    <p className={styles.subtitle}>
                        이미 계정이 있으신가요? {' '}
                        {/*로그인 페이지로 이동*/}
                        <span
                            className={styles.login}
                            onClick={() => navigate('/login')}
                            style={{cursor: 'pointer'}}
                        >
                            로그인
                        </span>
                    </p>

                    <form onSubmit={handleSubmit} className={styles.form}>
                        <h3 className={styles.subTitle}>계정 정보</h3>
                        <div className={styles.line}/>
                        {/*ID 입력창*/}
                        <Input
                            label="ID"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            fullWidth
                            required
                        />
                        {/*비밀번호 입력창*/}
                        <Input
                            label="비밀번호 - 영어, 숫자, 특수문자가 섞인 8글자 이상이어야 합니다"
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            fullWidth
                            required
                        />
                        {/*비밀번호 확인 입력창*/}
                        <Input
                            label="비밀번호 확인"
                            type="password"
                            name="confirmPassword"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            fullWidth
                            required
                        />

                        <h3 className={styles.subTitle}>기업 정보</h3>
                        <div className={styles.line}/>
                        {/*사업자등록번호 입력창*/}
                        <Input
                            label="사업자등록번호"
                            name="registrationNumber"
                            value={formData.registrationNumber}
                            onChange={handleChange}
                            placeholder="000-00-00000"
                            writable={false}
                            maxLength={12} // 000-00-00000 (12자리)
                            rightButton={
                                // 인증 버튼
                                <Button
                                    type="button"
                                    onClick={authenticateHandler}
                                    variant="secondary"
                                    size="small"
                                >
                                    인증
                                </Button>
                            }
                            className={styles.registrationInput}
                            required
                        />
                        {/*회사명 입력창*/}
                        <Input
                            label="회사명"
                            name="companyName"
                            value={formData.companyName}
                            writable={false}
                            onChange={handleChange}
                            fullWidth
                            required
                        />
                        {/*대표자명 입력창*/}
                        <Input
                            label="대표자 성명"
                            name="name"
                            value={formData.name}
                            writable={false}
                            onChange={handleChange}
                            fullWidth
                            required
                        />
                        {/*사업자등록증 등록*/}
                        <FileInput
                            label="사업자등록증"
                            onChange={handleFileChange}
                            value={formData.license}
                            accept="image/jpeg,image/png,application/pdf"
                        />

                        <h3 className={styles.subTitle}>담당자 정보</h3>
                        <div className={styles.line}/>
                        {/*담당자 이름 입력창*/}
                        <Input
                            label="담당자 성명"
                            name="managerName"
                            value={formData.managerName}
                            onChange={handleChange}
                            fullWidth
                            required
                        />
                        {/*담당자 전화번호 입력창*/}
                        <Input
                            label="담당자 전화번호"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            placeholder="010-1234-5678"
                            fullWidth
                            required
                        />
                        {/*이메일 입력창*/}
                        <Input
                            label="Email"
                            type="email"
                            name="email"
                            value={formData.email}
                            rightButton={
                                emailVerified ? (
                                    // 이메일 인증 성공 시 체크 아이콘 출력
                                    <CheckIcon className={styles.verifiedIcon}/>
                                ) : (
                                    // 이메일 인증 성공 전까진 코드 전송 버튼 출력
                                    <Button
                                        type="button"
                                        onClick={mailHandler}
                                        variant="secondary"
                                        size="small"
                                        disabled={!isValidEmail(formData.email)}
                                    >
                                        코드 전송
                                    </Button>
                                )
                            }
                            onChange={handleChange}
                            fullWidth
                            required
                            disabled={emailVerified}
                            writable={!emailVerified}
                        />
                        {/*인증코드 입력창*/}
                        <Input
                            label="인증코드"
                            name="code"
                            value={code}
                            rightButton={
                                !emailVerified && (
                                    // 메일 인증 성공 전까진 인증 버튼 출력
                                    <Button
                                        type="button"
                                        onClick={codeHandler}
                                        variant="secondary"
                                        size="small"
                                    >
                                        인증
                                    </Button>
                                )
                            }
                            onChange={handleCodeChange}
                            className={`${styles.codeInput} ${emailVerified ? styles.verifiedInput : ''}`}
                            required
                            disabled={emailVerified}
                            writable={!emailVerified}
                        />
                        {/*회원가입 버튼*/}
                        <div className={styles.buttonContainer}>
                            <Button
                                variant="secondary"
                                fullWidth
                                className={styles.back}
                                onClick={handelGoBack}
                            >
                                뒤로가기
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                fullWidth
                            >
                                제출
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
            {/*사업자등록번호 인증 팝업*/}
            <BusinessNumberPopUp
                isOpen={isAuthModalOpen}
                onClose={() => setIsAuthModalOpen(false)}
                onSuccess={handleAuthSuccess}
            />
        </div>
    );
};

export default EssentialRegisterPage;